export const getDiorQuery = (queryName, start, stop, rackTypes=[]) => {
  const durationQuantile = 0.96;
  return {
    total_racks_processed: `
      // Number of racks created between startTime and endTime.
      // Returns 0 if no rack is found.
      import "array"
      
      bucket = "engine"
      startTime = ${start}
      endTime = ${stop}
      
      rackCount =
          from(bucket: bucket)
              |> range(start: startTime, stop: endTime)
              |> filter(fn: (r) => r._measurement == "racks")
              |> filter(fn: (r) => r._field == "value")
              |> keep(columns: ["rack_id"])
              |> group()
              |> unique(column: "rack_id")
              |> count(column: "rack_id")
              |> rename(columns: {rack_id: "num_of_racks"})
      
      zeroCount =
          array.from(rows: [{num_of_racks: 0}])
      
      union(tables: [rackCount, zeroCount])
          |> group()
          |> max(column: "num_of_racks")
          |> yield(name: "num_of_racks")
    `,
    line_occupancy: `
       // Total time each process/step bath  process baths.
    
        import "array"
        
        bucket = "engine"
        startTime = ${start}
        endTime = ${stop}
        
        processPools = [
            "0", "4", "8", "11", "15", "16", "20", "21", "25", "29", "32", "36",
            "37", "41", "45", "49", "53", "57", "61", "65", "69", "73", "74",
        ]
        longDurationPools = ["15", "16", "65"]
        
        allProcessPools = array.from(
            rows: array.map(
                arr: processPools,
                fn: (x) => ({pool_id: x, _value: 0.0}),
            ),
        )
        
        base =
            from(bucket: bucket)
                |> range(start: startTime, stop: endTime)
                |> filter(fn: (r) => r._measurement == "dives")
                |> filter(fn: (r) => r._field == "duration")
                |> filter(fn: (r) => contains(value: r.pool_id, set: processPools))
                |> filter(fn: (r) => r._value > 0)
                |> filter(fn: (r) =>
                    if contains(value: r.pool_id, set: longDurationPools) then
                        r._value <= 800
                    else
                        r._value < 550
                )
                |> keep(columns: ["pool_id", "_value"])
        
        actualDurations =
            base
                |> group(columns: ["pool_id"])
                |> sum(column: "_value")
                |> keep(columns: ["pool_id", "_value"])
                |> map(fn: (r) => ({
                    pool_id: r.pool_id,
                    _value: float(v: r._value),
                }))
        
        union(tables: [allProcessPools, actualDurations])
            |> group(columns: ["pool_id"])
            |> sum(column: "_value")
            |> map(fn: (r) => ({
                pool_id: r.pool_id,
                pool_id_num: int(v: r.pool_id),
                total_duration_seconds: r._value,
            }))
            |> group()
            |> sort(columns: ["pool_id_num"])
            |> keep(columns: ["pool_id", "total_duration_seconds"])
    `,
    racks_per_process: `
      import "array"

      bucket = "engine"
      startTime = ${start}
      endTime = ${stop}
      
      rackTypes = ${JSON.stringify(rackTypes)}
      
      allRackTypes = array.from(
          rows: array.map(
              arr: rackTypes,
              fn: (x) => ({type: x, num_of_racks: 0}),
          ),
      )
      
      actualRackCounts =
          from(bucket: bucket)
              |> range(start: startTime, stop: endTime)
              |> filter(fn: (r) => r._measurement == "racks")
              |> filter(fn: (r) => r._field == "value")
              |> filter(fn: (r) => contains(value: r.type, set: rackTypes))
              |> keep(columns: ["type", "rack_id"])
              |> group(columns: ["type"])
              |> unique(column: "rack_id")
              |> count(column: "rack_id")
              |> rename(columns: {rack_id: "num_of_racks"})
              |> keep(columns: ["type", "num_of_racks"])
      
      union(tables: [allRackTypes, actualRackCounts])
          |> group(columns: ["type"])
          |> sum(column: "num_of_racks")
          |> group()
          |> sort(columns: ["type"])
    `,
    baths_mean: `
      // Average duration (s) and dips per process bath.

      import "array"
      
      bucket = "engine"
      startTime = ${start}
      endTime = ${stop}
      
      processPools = [
          "0", "4", "8", "11", "15", "16", "20", "21", "25", "29", "32", "36",
          "37", "41", "45", "49", "53", "57", "61", "65", "69", "73", "74",
      ]
      longDurationPools = ["15", "16", "65"]
      
      zeroStats = array.from(
          rows: array.map(
              arr: processPools,
              fn: (x) => ({pool_id: x, avg_duration_seconds: 0.0, dips: 0}),
          ),
      )
      
      base =
          from(bucket: bucket)
              |> range(start: startTime, stop: endTime)
              |> filter(fn: (r) => r._measurement == "dives")
              |> filter(fn: (r) => r._field == "duration")
              |> filter(fn: (r) => contains(value: r.pool_id, set: processPools))
              |> filter(fn: (r) => r._value > 0)
              |> filter(fn: (r) =>
                  if contains(value: r.pool_id, set: longDurationPools) then
                      r._value <= 800
                  else
                      r._value < 550
              )
              |> keep(columns: ["pool_id", "_value"])
      
      averageStats =
          base
              |> group(columns: ["pool_id"])
              |> mean(column: "_value")
              |> rename(columns: {_value: "avg_duration_seconds"})
              |> keep(columns: ["pool_id", "avg_duration_seconds"])
      
      dipStats =
          base
              |> group(columns: ["pool_id"])
              |> count(column: "_value")
              |> rename(columns: {_value: "dips"})
              |> keep(columns: ["pool_id", "dips"])
      
      actualStats =
          join(tables: {averages: averageStats, dips: dipStats}, on: ["pool_id"])
              |> keep(columns: ["pool_id", "avg_duration_seconds", "dips"])
      
      union(tables: [zeroStats, actualStats])
          |> group(columns: ["pool_id"])
          |> max(column: "avg_duration_seconds")
          |> map(fn: (r) => ({
              pool_id: r.pool_id,
              pool_id_num: int(v: r.pool_id),
              avg_duration_seconds: r.avg_duration_seconds,
              dips: r.dips,
          }))
          |> group()
          |> sort(columns: ["pool_id_num"])
          |> keep(columns: ["pool_id", "avg_duration_seconds", "dips"])
    `,
    baths_maintenance: `
      // Total duration (s) and dips per process bath. 

      import "array"
      
      bucket = "engine"
      startTime = -100y
      endTime = ${stop}
      
      processPools = [
          "0", "4", "8", "11", "15", "16", "20", "21", "25", "29", "32", "36",
          "37", "41", "45", "49", "53", "57", "61", "65", "69","70","71", "73", "74",
      ]
      longDurationPools = ["15", "16", "65"]
      
      zeroStats = array.from(
          rows: array.map(
              arr: processPools,
              fn: (x) => ({pool_id: x, total_duration_seconds: 0.0, dips: 0}),
          ),
      )
      
      base =
          from(bucket: bucket)
              |> range(start: startTime, stop: endTime)
              |> filter(fn: (r) => r._measurement == "dives")
              |> filter(fn: (r) => r._field == "duration")
              |> filter(fn: (r) => contains(value: r.pool_id, set: processPools))
              |> filter(fn: (r) => r._value > 0)
              |> filter(fn: (r) =>
                  if contains(value: r.pool_id, set: longDurationPools) then
                      r._value <= 800
                  else
                      r._value < 550
              )
              |> keep(columns: ["pool_id", "_value"])
      
      durationStats =
          base
              |> group(columns: ["pool_id"])
              |> sum(column: "_value")
              |> rename(columns: {_value: "total_duration_seconds"})
              |> map(fn: (r) => ({pool_id: r.pool_id, total_duration_seconds: float(v: r.total_duration_seconds)}))
      
      dipStats =
          base
              |> group(columns: ["pool_id"])
              |> count(column: "_value")
              |> rename(columns: {_value: "dips"})
      
      actualStats =
          join(tables: {durations: durationStats, dips: dipStats}, on: ["pool_id"])
              |> keep(columns: ["pool_id", "total_duration_seconds", "dips"])
      
      union(tables: [zeroStats, actualStats])
          |> group(columns: ["pool_id"])
          |> max(column: "total_duration_seconds")
          |> map(fn: (r) => ({
              pool_id: r.pool_id,
              pool_id_num: int(v: r.pool_id),
              total_duration_seconds: r.total_duration_seconds,
              dips: r.dips,
          }))
          |> group()
          |> sort(columns: ["pool_id_num"])
          |> keep(columns: ["pool_id", "total_duration_seconds", "dips"])
    `
  }[queryName];
};
export const getCurrentDay = (delta_days = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(date.getDate() + delta_days);

  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(start);
  stop.setDate(stop.getDate() + 1);

  return { start: start.getTime(), stop: stop.getTime() };
};

export const getCurrentWeek = (delta_weeks = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(date.getDate() + delta_weeks * 7);

  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const start = new Date(date);
  start.setDate(date.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(start);
  stop.setDate(start.getDate() + 7);

  return { start: start.getTime(), stop: stop.getTime() };
};

export const getLastRollingDays = (days = 7, now = new Date()) => {
  const stop = new Date(now);
  stop.setHours(0, 0, 0, 0);

  const start = new Date(stop);
  start.setDate(start.getDate() - days);

  return { start: start.getTime(), stop: stop.getTime() };
};

export const getCurrentMonth = (delta_months = 0, now = new Date()) => {
  const start = new Date(now);
  start.setMonth(start.getMonth() + delta_months, 1);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(start);
  stop.setMonth(stop.getMonth() + 1);

  return { start: start.getTime(), stop: stop.getTime() };
};

export const getWorkedSecondsToday = (rangeStart = null, includeToday = true) => {
  const now = new Date();

  const isWorkingDay = (date) => {
    const day = date.getDay();

    return day !== 0 && day !== 6;
  };

  const getPeriodSeconds = (date, startHour, endHour, referenceDate) => {
    const start = new Date(date);
    start.setHours(startHour, 0, 0, 0);

    const end = new Date(date);
    end.setHours(endHour, 0, 0, 0);

    if (referenceDate <= start) return 0;
    if (referenceDate >= end) return (end - start) / 1000;

    return (referenceDate - start) / 1000;
  };

  const getFullDaySeconds = (date) => {
    if (!isWorkingDay(date)) return 0;

    return 8 * 3600;
  };

  const getCurrentDaySeconds = (ignoreWeekend = false) => {
    if (ignoreWeekend && !isWorkingDay(now)) return 0;

    return (
      getPeriodSeconds(now, 8, 13, now) +
      getPeriodSeconds(now, 14, 17, now)
    );
  };

  if (!rangeStart) return getCurrentDaySeconds();

  let seconds = 0;
  const currentDayStart = new Date(now);
  currentDayStart.setHours(0, 0, 0, 0);

  const stopDay = new Date(currentDayStart);

  const day = new Date(rangeStart);
  day.setHours(0, 0, 0, 0);

  while (day < stopDay) {
    seconds += getFullDaySeconds(day);
    day.setDate(day.getDate() + 1);
  }

  return seconds + (includeToday ? getCurrentDaySeconds(true) : 0);
};